<template>
	<TutorialWelcomeModal
		:visible="visible"
		title="Meet the Updated ResumAI Scoring"
		:body="welcomeBody"
		@begin="handleStartOnboarding"
		@close="handleCloseModal"
	/>

	<TutorialTour
		:active="tourActive"
		:step="tourStep"
		:step-index="tourStepIndex"
		:step-count="tourTotalSteps"
		@next="tourNext"
		@previous="tourPrevious"
		@close="tourExit"
	/>
</template>

<script setup>
import { ref } from 'vue';

import TutorialWelcomeModal from '../components/tutorial/tutorial-welcome-modal.vue';
import TutorialTour from '../components/tutorial/tutorial-tour.vue';

import { useTutorialTour } from '../composables/use-tutorial-tour';
import { setAuthState } from '../composables/auth';
import { markResumeScoreWhatsNewPassed } from '../api/profile';

import { useResumeScorerStore } from '../store/scorer.store';

import { delay } from '../utils/helpers';
import { hasTargetRole } from '../utils/resume-score-tutorial';
import { canShowResumeScoreWhatsNewTutorial, RESUME_SCORE_WHATS_NEW_TUTORIAL_FLAG } from '../utils/resume-score-whats-new-tutorial';

// Existing-user "Meet the Updated ResumAI Scoring" walkthrough (EN-806) — a shorter, 3-step
// re-introduction shown once to users who already have real resume content (i.e. already have
// `builder_passed` set), covering only what changed in scoring instead of re-explaining the
// whole builder like the new-user tour (modals/builder-onboarding.vue, EN-805) does. Built on
// the same shared tutorial-tour/tutorial-welcome-modal primitives as that tour, with its own
// gating flag (see utils/resume-score-whats-new-tutorial.js) so the two tours never both fire
// for the same person — a brand-new user going through EN-805 never becomes eligible here
// because that tour pre-emptively marks this flag too when it finishes (see the comment in
// modals/builder-onboarding.vue's persistTutorialPassed).
//
// Deliberately does NOT touch the onboarding store's `isOnboarding`/`onboardingStep` state the
// way builder-onboarding.vue does — that flag switches several builder components (bullet
// generator, section visibility) into a scripted, mock-data "new resume" demo mode meant for
// users with no real content yet. Existing users going through this tour have real resumes, so
// that mock-data behavior must stay off.
const { getUser, setUser } = setAuthState();

const visible = ref(canShowResumeScoreWhatsNewTutorial(getUser()));

const welcomeBody = 'We\'ve updated how your resume is scored and made recommendations clearer and more helpful, so you can make improvements faster. Take a quick look at what\'s new.';

const scorerStore = useResumeScorerStore();

const {
	active: tourActive,
	currentStep: tourStep,
	stepIndex: tourStepIndex,
	totalSteps: tourTotalSteps,
	start: tourStart,
	next: tourNext,
	previous: tourPrevious,
	exit: tourExit,
} = useTutorialTour();

const persistTutorialPassed = () => {
	const user = getUser();

	if (! user || user.options?.[RESUME_SCORE_WHATS_NEW_TUTORIAL_FLAG]) {
		return;
	}

	setUser({ ...user, options: { ...user.options, [RESUME_SCORE_WHATS_NEW_TUTORIAL_FLAG]: true } });

	markResumeScoreWhatsNewPassed().catch(() => {
		// the local flag is already set; a failed sync just means it may retry next session
	});
};

const handleCloseModal = () => {
	visible.value = false;
	persistTutorialPassed();
};

const buildSteps = () => {
	const roleIsSet = hasTargetRole(getUser());

	return [
		{
			target: '#resume-score-target-anchor',
			position: 'left',
			title: 'Your Target Role',
			body: roleIsSet
				? 'Your resume score is now based on a specific target role and seniority level. We set one based on your resume — update it here anytime.'
				: 'Your resume score is now based on a specific target role and seniority level. Set or update it here anytime.',
			onEnter: () => scorerStore.setShowResumeScorer(),
		},
		{
			target: '#resume-score-panel-anchor',
			position: 'left',
			title: 'Resume Score & Recommendations',
			body: 'We rebuilt how your resume score works, and recalculated your score under the new system. It\'s now always visible, with a clear breakdown and recommendations.  Aim for 75+ to move forward with job matching — work through the recommendations to get there.',
		},
		{
			// EN-808 ("ResumAI: Header Updates") moved Share/Download out of the primary
			// header row into a "..." overflow menu and replaced them with a forward-moving
			// "Save & Continue" primary action — this branch now includes that change, so
			// the anchor below points at the real, current header layout this copy describes.
			target: '#onboarding-header-actions-anchor',
			position: 'bottom',
			title: 'Updated Main Actions',
			body: 'Save & Continue now takes you straight to your next step, so you can get to applying faster. Download and Share options are still there, just under the ⋮ menu.',
		},
	];
};

const handleStartOnboarding = async () => {
	visible.value = false;

	// let the welcome modal's close transition finish before measuring the first target
	await delay(200);

	const steps = buildSteps();

	if (! steps.length) {
		persistTutorialPassed();
		return;
	}

	tourStart(steps, {
		onClose: () => {
			persistTutorialPassed();
		},
	});
};
</script>
